import type {
  HealthUnitWeekList,
  ListStationsResponse,
  StationWeekList
} from '@/Network/Types/Responses/StationResponses'
import type { LensGroupBy } from '@/Network/Types/Requests/DashboardSavedViewRequests'
import type { StationToUpdate } from '@/modules/company/Dashboard/modals/createStation/helpers'
import { catalogDisplayName } from '@/utils/stationDisplay'
import type { DayColumn } from './monthCalendar'
import { isUnidentifiedCoverage } from '@/utils/coverage'
import type { CoverageCounts } from '@/Network/Types/Responses/CoverageResponses'

export type GridUnitRef = {
  healthUnitId: number
  healthUnitName: string
  healthUnitLatitude: string
  healthUnitLongitude: string
  healthUnitIsActive: boolean
}

export type GridCardRef = {
  card: StationWeekList
  companyId: number
  companyName: string
  unit: GridUnitRef
  scheduleId: number
  scheduleName: string
  scheduleTimezone: string
  shiftId: number
  shiftTitle: string
  hourTitle: string
  positionTitle: string
  dayIndex: number
}

export type GridRow = {
  key: string
  scheduleId: number
  scheduleName: string
  healthUnitId: number
  shiftId: number
  shiftKey: string
  shiftTitle: string
  hourTitle: string
  positionTitle: string
  /** `cells[dayIndex]` = plantões daquele dia; o índice é o mesmo do `tableHead`. */
  cells: GridCardRef[][]
}

export type GridGroup = {
  key: string
  label: string
  sublabel: string | null
  timezone: string | null
  scheduleNames: string[]
  rows: GridRow[]
}

export type GridBand = {
  key: string
  kind: 'unit' | 'specialty' | 'shift' | 'all'
  label: string
  companyName: string | null
  unitIsActive: boolean
  groups: GridGroup[]
}

export type GridVisibility = {
  canReadSchedule: (scheduleId: number) => boolean
  canShowUnit: (unit: HealthUnitWeekList) => boolean
}

export const NO_VALUE_KEY = 'none'

export const sectorKeyOf = (card: StationWeekList): string =>
  card.sector ? `s:${card.sector.id}` : NO_VALUE_KEY

export const specialtyKeyOf = (card: StationWeekList): string =>
  card.specialty
    ? `${card.specialty.source === 'official' ? 'o' : 'c'}:${card.specialty.id}`
    : NO_VALUE_KEY

type SourceRow = GridRow & {
  unit: GridUnitRef
  companyId: number
  companyName: string
  timezone: string
}

/**
 * Esqueleto da lente Escala: toda unidade visível e toda escala legível, mesmo sem linha
 * (paridade com o DashboardView antigo, que desenhava "Empresa - Unidade" e o nome da escala
 * sem posições).
 */
type ScheduleSkeleton = Map<string, GridBand>

const unitBandKey = (companyId: number, healthUnitId: number) => `${companyId}|${healthUnitId}`

function collectSourceRows(
  data: ListStationsResponse | undefined,
  visibility: GridVisibility,
  skeleton?: ScheduleSkeleton
): SourceRow[] {
  const rows: SourceRow[] = []
  ;(data?.data ?? []).forEach((company) => {
    company.healthUnities.forEach((unit) => {
      if (!visibility.canShowUnit(unit)) return
      const unitRef: GridUnitRef = {
        healthUnitId: unit.healthUnitId,
        healthUnitName: unit.healthUnitName,
        healthUnitLatitude: unit.healthUnitLatitude,
        healthUnitLongitude: unit.healthUnitLongitude,
        healthUnitIsActive: unit.healthUnitIsActive !== false
      }
      const band: GridBand = {
        key: unitBandKey(company.companyId, unit.healthUnitId),
        kind: 'unit',
        label: unit.healthUnitName,
        companyName: company.companyName,
        unitIsActive: unitRef.healthUnitIsActive,
        groups: []
      }
      if (skeleton && !skeleton.has(band.key)) skeleton.set(band.key, band)
      ;(unit.schedules ?? []).forEach((schedule) => {
        if (!visibility.canReadSchedule(schedule.scheduleId)) return
        const skeletonBand = skeleton?.get(band.key)
        if (
          skeletonBand &&
          !skeletonBand.groups.some((g) => g.key === `e:${schedule.scheduleId}`)
        ) {
          skeletonBand.groups.push({
            key: `e:${schedule.scheduleId}`,
            label: schedule.scheduleName,
            sublabel: null,
            timezone: schedule.scheduleTimezone,
            scheduleNames: [schedule.scheduleName],
            rows: []
          })
        }
        ;(schedule.shifts ?? []).forEach((shift) => {
          ;(shift.hours ?? []).forEach((hour) => {
            ;(hour.positions ?? []).forEach((position, positionIndex) => {
              const cells = (position.stations ?? []).map((dayCards, dayIndex) =>
                dayCards.map<GridCardRef>((card) => ({
                  card,
                  companyId: company.companyId,
                  companyName: company.companyName,
                  unit: unitRef,
                  scheduleId: schedule.scheduleId,
                  scheduleName: schedule.scheduleName,
                  scheduleTimezone: schedule.scheduleTimezone,
                  shiftId: shift.shiftId,
                  shiftTitle: shift.title,
                  hourTitle: hour.title,
                  positionTitle: position.title,
                  dayIndex
                }))
              )
              rows.push({
                key: `${schedule.scheduleId}|${shift.shiftId}|${hour.title}|${positionIndex}`,
                scheduleId: schedule.scheduleId,
                scheduleName: schedule.scheduleName,
                healthUnitId: unit.healthUnitId,
                shiftId: shift.shiftId,
                shiftKey: `${shift.shiftId}|${hour.title}`,
                shiftTitle: shift.title,
                hourTitle: hour.title,
                positionTitle: position.title,
                cells,
                unit: unitRef,
                companyId: company.companyId,
                companyName: company.companyName,
                timezone: schedule.scheduleTimezone
              })
            })
          })
        })
      })
    })
  })
  return rows
}

/** Divide a linha de posição pela chave do plantão (setor/especialidade): P1, sem herdar da posição. */
export function partitionRow<R extends GridRow>(
  row: R,
  keyOf: (ref: GridCardRef) => string
): Map<string, R> {
  const parts = new Map<string, R>()
  row.cells.forEach((dayCards, dayIndex) => {
    dayCards.forEach((ref) => {
      const key = keyOf(ref)
      let part = parts.get(key)
      if (!part) {
        part = { ...row, key: `${row.key}#${key}`, cells: row.cells.map(() => []) }
        parts.set(key, part)
      }
      part.cells[dayIndex].push(ref)
    })
  })
  return parts
}

const compareNames = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' })

const firstRef = (row: GridRow): GridCardRef | undefined => row.cells.find((day) => day.length)?.[0]

const uniqueScheduleNames = (rows: GridRow[]): string[] => [
  ...new Set(rows.map((r) => r.scheduleName))
]

function stripSource(row: SourceRow): GridRow {
  return {
    key: row.key,
    scheduleId: row.scheduleId,
    scheduleName: row.scheduleName,
    healthUnitId: row.healthUnitId,
    shiftId: row.shiftId,
    shiftKey: row.shiftKey,
    shiftTitle: row.shiftTitle,
    hourTitle: row.hourTitle,
    positionTitle: row.positionTitle,
    cells: row.cells
  }
}

type WorkGroup = GridGroup & { order: number; isNone: boolean }

function sortGroups(groups: WorkGroup[]): GridGroup[] {
  return [...groups]
    .sort(
      (a, b) =>
        a.order - b.order || Number(a.isNone) - Number(b.isNone) || compareNames(a.label, b.label)
    )
    .map((g) => ({
      key: g.key,
      label: g.label,
      sublabel: g.sublabel,
      timezone: g.timezone,
      scheduleNames: g.scheduleNames,
      rows: g.rows
    }))
}

function groupBySchedule(rows: SourceRow[], skeleton: ScheduleSkeleton): GridBand[] {
  rows.forEach((row) => {
    const group = skeleton
      .get(unitBandKey(row.companyId, row.healthUnitId))
      ?.groups.find((g) => g.key === `e:${row.scheduleId}`)
    group?.rows.push(stripSource(row))
  })
  return [...skeleton.values()]
}

function sectorGroupFor(
  groups: Map<string, WorkGroup>,
  key: string,
  part: SourceRow,
  unitOrder: number,
  withUnitSublabel: boolean
): WorkGroup {
  let group = groups.get(key)
  if (!group) {
    const ref = firstRef(part)
    const isNone = !ref?.card.sector
    group = {
      key,
      label: catalogDisplayName(ref?.card.sector ?? null, 'Sem setor'),
      sublabel: withUnitSublabel ? part.unit.healthUnitName : null,
      timezone: null,
      scheduleNames: [],
      rows: [],
      order: unitOrder,
      isNone
    }
    groups.set(key, group)
  }
  return group
}

function groupBySector(rows: SourceRow[]): GridBand[] {
  const bands = new Map<string, { band: GridBand; groups: Map<string, WorkGroup> }>()
  rows.forEach((row) => {
    const bandKey = unitBandKey(row.companyId, row.healthUnitId)
    let entry = bands.get(bandKey)
    if (!entry) {
      entry = {
        band: {
          key: bandKey,
          kind: 'unit',
          label: row.unit.healthUnitName,
          companyName: row.companyName,
          unitIsActive: row.unit.healthUnitIsActive,
          groups: []
        },
        groups: new Map()
      }
      bands.set(bandKey, entry)
    }
    const groups = entry.groups
    partitionRow(row, (ref) => sectorKeyOf(ref.card)).forEach((part, sectorKey) => {
      sectorGroupFor(groups, sectorKey, part, 0, false).rows.push(stripSource(part))
    })
  })
  return [...bands.values()]
    .filter(({ groups }) => groups.size > 0)
    .map(({ band, groups }) => ({
      ...band,
      groups: sortGroups(
        [...groups.values()].map((g) => ({ ...g, scheduleNames: uniqueScheduleNames(g.rows) }))
      )
    }))
}

const KEY_SEPARATOR = '\u0000'

function groupByUnitSectorInside(
  rows: SourceRow[],
  bandKeyOf: (ref: GridCardRef) => string,
  makeBand: (key: string, ref: GridCardRef) => GridBand
): Map<string, { band: GridBand; groups: Map<string, WorkGroup>; stations: number; open: number }> {
  const unitOrder = new Map<number, number>()
  const bands = new Map<
    string,
    { band: GridBand; groups: Map<string, WorkGroup>; stations: number; open: number }
  >()
  rows.forEach((row) => {
    if (!unitOrder.has(row.healthUnitId)) unitOrder.set(row.healthUnitId, unitOrder.size)
    // '\u0000' não aparece em título de turno/ids: a chave composta não quebra (ex.: "Dia || Noite").
    partitionRow(row, (ref) => `${bandKeyOf(ref)}${KEY_SEPARATOR}${sectorKeyOf(ref.card)}`).forEach(
      (part, compositeKey) => {
        const [bandKey, sectorKey] = compositeKey.split(KEY_SEPARATOR)
        let entry = bands.get(bandKey)
        if (!entry) {
          entry = {
            band: makeBand(bandKey, firstRef(part)!),
            groups: new Map(),
            stations: 0,
            open: 0
          }
          bands.set(bandKey, entry)
        }
        const groupKey = `${row.companyId}|${row.healthUnitId}|${sectorKey}`
        sectorGroupFor(
          entry.groups,
          groupKey,
          part,
          unitOrder.get(row.healthUnitId) ?? 0,
          true
        ).rows.push(stripSource(part))
        const counted = countCards([part])
        entry.stations += counted.stations
        entry.open += counted.open
      }
    )
  })
  return bands
}

function finishBands(
  entries: Map<
    string,
    { band: GridBand; groups: Map<string, WorkGroup>; stations: number; open: number }
  >,
  sortByOpen: boolean
): GridBand[] {
  const list = [...entries.values()]
  if (sortByOpen) {
    list.sort(
      (a, b) =>
        Number(a.band.key === NO_VALUE_KEY) - Number(b.band.key === NO_VALUE_KEY) ||
        b.open - a.open ||
        b.stations - a.stations ||
        compareNames(a.band.label, b.band.label)
    )
  }
  return list.map(({ band, groups }) => ({
    ...band,
    groups: sortGroups(
      [...groups.values()].map((g) => ({ ...g, scheduleNames: uniqueScheduleNames(g.rows) }))
    )
  }))
}

function groupBySpecialty(rows: SourceRow[]): GridBand[] {
  const entries = groupByUnitSectorInside(
    rows,
    (ref) => specialtyKeyOf(ref.card),
    (key, ref) => ({
      key,
      kind: 'specialty',
      label: catalogDisplayName(ref.card.specialty ?? null, 'Sem especialidade'),
      companyName: null,
      unitIsActive: true,
      groups: []
    })
  )
  return finishBands(entries, true)
}

function groupByShift(rows: SourceRow[]): GridBand[] {
  const entries = groupByUnitSectorInside(
    rows,
    (ref) => ref.shiftTitle.trim().toLowerCase(),
    (key, ref) => ({
      key,
      kind: 'shift',
      label: ref.shiftTitle,
      companyName: null,
      unitIsActive: true,
      groups: []
    })
  )
  return finishBands(entries, false)
}

export const ALL_STATIONS_KEY = 'all'
export const ALL_STATIONS_LABEL = 'Todos os plantões'

/** Minutos do dia do 1º horário do rótulo ("07:00-19:00" → 420); sem hora → fim da fila. */
export function hourTitleStartMinutes(hourTitle: string): number {
  const match = /(\d{1,2})(?::(\d{2}))?/.exec(hourTitle ?? '')
  if (!match) return Number.POSITIVE_INFINITY
  return Number(match[1]) * 60 + Number(match[2] ?? 0)
}

/**
 * Lente "Sem agrupamento" (spec §4.4): uma banda só, com as linhas em ordem de início,
 * depois unidade, depois escala. Linha sem plantão no período não entra (sem o esqueleto
 * da lente Escala não há o que desenhar nela).
 */
function groupByNone(rows: SourceRow[]): GridBand[] {
  const withCards = rows.filter((row) => row.cells.some((day) => day.length))
  if (!withCards.length) return []
  const sorted = [...withCards].sort(
    (a, b) =>
      hourTitleStartMinutes(a.hourTitle) - hourTitleStartMinutes(b.hourTitle) ||
      compareNames(a.unit.healthUnitName, b.unit.healthUnitName) ||
      compareNames(a.scheduleName, b.scheduleName) ||
      a.key.localeCompare(b.key)
  )
  const groupRows = sorted.map(stripSource)
  return [
    {
      key: ALL_STATIONS_KEY,
      kind: 'all',
      label: ALL_STATIONS_LABEL,
      companyName: null,
      unitIsActive: true,
      groups: [
        {
          key: ALL_STATIONS_KEY,
          label: ALL_STATIONS_LABEL,
          sublabel: null,
          timezone: null,
          scheduleNames: uniqueScheduleNames(groupRows),
          rows: groupRows
        }
      ]
    }
  ]
}

export function buildGridBands(
  data: ListStationsResponse | undefined,
  groupBy: LensGroupBy,
  visibility: GridVisibility
): GridBand[] {
  switch (groupBy) {
    case 'setor':
      return groupBySector(collectSourceRows(data, visibility))
    case 'especialidade':
      return groupBySpecialty(collectSourceRows(data, visibility))
    case 'turno':
      return groupByShift(collectSourceRows(data, visibility))
    case 'nenhum':
      return groupByNone(collectSourceRows(data, visibility))
    default: {
      // 'escala' e qualquer valor desconhecido (view salva/URL antiga) caem na hierarquia de hoje.
      const skeleton: ScheduleSkeleton = new Map()
      return groupBySchedule(collectSourceRows(data, visibility, skeleton), skeleton)
    }
  }
}

/**
 * Contadores da grade. `unidentified` = "Ocupado sem identificação": coberto (fora de `open`)
 * e sem médico — quem precisar de "com médico" usa `stations - open - unidentified`.
 */
export type CardCounts = { stations: number; open: number; unidentified: number }

function tally(counts: CardCounts, ref: GridCardRef): void {
  counts.stations += 1
  // Checado ANTES de `opened` (o BE manda opened=false para o UNIDENTIFIED).
  if (isUnidentifiedCoverage(ref.card)) counts.unidentified += 1
  else if (ref.card.opened) counts.open += 1
}

export function countCards(rows: GridRow[], from = 0, to = Number.POSITIVE_INFINITY): CardCounts {
  const counts: CardCounts = { stations: 0, open: 0, unidentified: 0 }
  rows.forEach((row) => {
    row.cells.slice(from, to).forEach((dayCards) => {
      dayCards.forEach((ref) => tally(counts, ref))
    })
  })
  return counts
}

/** Contagem de uma lista solta de plantões (agenda do dia no celular), com a regra do `tally`. */
export function countRefs(refs: GridCardRef[]): CardCounts {
  const counts: CardCounts = { stations: 0, open: 0, unidentified: 0 }
  refs.forEach((ref) => tally(counts, ref))
  return counts
}

/** Contagem de um bloco de dias; dias de outro mês não contam (Q23: coluna vazia). */
export function countCardsInDays(
  rows: GridRow[],
  days: DayColumn[],
  dayOffset: number
): CardCounts {
  const counts: CardCounts = { stations: 0, open: 0, unidentified: 0 }
  days.forEach((day, i) => {
    if (day.isOutsideMonth) return
    rows.forEach((row) => {
      ;(row.cells[dayOffset + i] ?? []).forEach((ref) => tally(counts, ref))
    })
  })
  return counts
}

/** `weekdayLong`: rótulo do `tableHead` do BE na Semana (desktop), como no DashboardView antigo. */
export type GridDayColumn = DayColumn & { weekdayLong?: string }

/** Divisor horizontal da linha — mesma hierarquia do DashboardView antigo (escala › turno › horário). */
export type GridRowBoundary = 'escala' | 'turno' | 'horario' | null

export type GridRowLayout = {
  row: GridRow
  shiftSpan: number
  isGroupStart: boolean
  isShiftStart: boolean
  boundary: GridRowBoundary
}

export function layoutGroupRows(rows: GridRow[]): GridRowLayout[] {
  return rows.map((row, index) => {
    const previous = index > 0 ? rows[index - 1] : null
    const isShiftStart = !previous || previous.shiftKey !== row.shiftKey
    let shiftSpan = 0
    if (isShiftStart) {
      shiftSpan = 1
      while (index + shiftSpan < rows.length && rows[index + shiftSpan].shiftKey === row.shiftKey)
        shiftSpan += 1
    }
    let boundary: GridRowBoundary = null
    if (!previous) boundary = 'escala'
    else if (previous.scheduleId !== row.scheduleId || previous.shiftId !== row.shiftId)
      boundary = 'turno'
    else if (isShiftStart) boundary = 'horario'
    return { row, shiftSpan, isGroupStart: index === 0, isShiftStart, boundary }
  })
}

export function countSchedules(data?: ListStationsResponse): number {
  return (data?.data ?? []).reduce(
    (total, company) =>
      total + company.healthUnities.reduce((sum, unit) => sum + (unit.schedules?.length ?? 0), 0),
    0
  )
}

export function mergeStationTrees(
  base: ListStationsResponse,
  extra: ListStationsResponse
): ListStationsResponse {
  const cloneUnit = (unit: HealthUnitWeekList): HealthUnitWeekList => ({
    ...unit,
    schedules: [...(unit.schedules ?? [])]
  })
  const cloneCompany = (company: ListStationsResponse['data'][number]) => ({
    ...company,
    healthUnities: (company.healthUnities ?? []).map(cloneUnit)
  })
  const companies = (base.data ?? []).map(cloneCompany)
  const companyIndex = new Map(companies.map((company, index) => [company.companyId, index]))
  ;(extra.data ?? []).forEach((company) => {
    const ci = companyIndex.get(company.companyId)
    if (ci === undefined) {
      companyIndex.set(company.companyId, companies.length)
      companies.push(cloneCompany(company))
      return
    }
    const target = companies[ci]
    const unitIndex = new Map(target.healthUnities.map((unit, index) => [unit.healthUnitId, index]))
    ;(company.healthUnities ?? []).forEach((unit) => {
      const ui = unitIndex.get(unit.healthUnitId)
      if (ui === undefined) {
        unitIndex.set(unit.healthUnitId, target.healthUnities.length)
        target.healthUnities.push(cloneUnit(unit))
        return
      }
      const targetUnit = target.healthUnities[ui]
      const seen = new Set(targetUnit.schedules.map((schedule) => schedule.scheduleId))
      ;(unit.schedules ?? []).forEach((schedule) => {
        if (!seen.has(schedule.scheduleId)) targetUnit.schedules.push(schedule)
      })
    })
  })
  const coverageCounts = sumCoverageCounts(base.coverageCounts, extra.coverageCounts)
  return {
    ...base,
    count: base.count + extra.count,
    data: companies,
    totalSchedules: extra.totalSchedules ?? base.totalSchedules,
    coverageCounts
  }
}

/**
 * O BE conta os chips do tri-estado só nas escalas de cada lote ("Carregar mais"):
 * os lotes são disjuntos, então somar dá a contagem das escalas carregadas. Sem
 * contadores de um dos lados (BE antigo) não inventa zeros.
 */
function sumCoverageCounts(
  base: CoverageCounts | undefined,
  extra: CoverageCounts | undefined
): CoverageCounts | undefined {
  if (!base || !extra) return undefined
  return {
    OPEN: base.OPEN + extra.OPEN,
    DOCTOR: base.DOCTOR + extra.DOCTOR,
    UNIDENTIFIED: base.UNIDENTIFIED + extra.UNIDENTIFIED
  }
}

/** Payload do clique no card — o mesmo que `DashboardView.handleClickStation` montava, mais setor/especialidade. */
export function toStationToUpdate(ref: GridCardRef): StationToUpdate {
  const { card } = ref
  return {
    inCash: card.inCash,
    stationId: card.stationId,
    companyId: ref.companyId,
    doctorId: card.doctorId,
    doctorStationId: card.doctorStationId ?? null,
    date: card.date,
    startHour: card.startHour,
    endHour: card.endHour,
    timezone: card.timezone,
    stationType: card.stationType,
    ourTeam: card.ourTeam,
    value: card.value,
    advancePaid: card.advancePaid,
    advanceRequested: card.advanceRequested,
    modifiedValue: card.modifiedValue,
    doctorPending: card.doctorPending,
    urlConviteDoctor: card.urlConviteDoctor,
    observations: [],
    checks: card.checks.map((check) => ({
      ...check,
      checkin: String(check.checkin),
      checkout: check.checkout ? String(check.checkout) : undefined,
      isMain: check.isMain ?? false
    })),
    position: card.position,
    healthUnitLatitude: ref.unit.healthUnitLatitude,
    healthUnitLongitude: ref.unit.healthUnitLongitude,
    stationValue: card.stationValue,
    userSponsor: card.doctorStationUserSponsor,
    scheduleId: card.scheduleId,
    sector: card.sector ?? null,
    specialty: card.specialty ?? null
  }
}
